import { z } from 'zod';

import {
  REGISTER_ORDER,
  SCOPE_ORDER,
  type PostScope,
  type PostType,
} from '@/features/posts/registers';

export const FEED_SORTS = ['hot', 'new', 'top'] as const;
export const FEED_WINDOWS = ['today', 'week', 'month', 'all'] as const;

export type FeedSort = (typeof FEED_SORTS)[number];
export type FeedWindow = (typeof FEED_WINDOWS)[number];

export interface FeedFilters {
  /** Empty string means the global feed. */
  country: string;
  town: string;
  types: PostType[];
  scopes: PostScope[];
  tags: string[];
  sort: FeedSort;
  window: FeedWindow;
  following: boolean;
}

export const DEFAULT_FILTERS: FeedFilters = {
  country: '',
  town: '',
  types: [...REGISTER_ORDER],
  scopes: [...SCOPE_ORDER],
  tags: [],
  sort: 'hot',
  window: 'week',
  following: false,
};

const filtersSchema = z.object({
  country: z.string().default(''),
  town: z.string().default(''),
  types: z.array(z.enum(['GOOD', 'BAD', 'IDEAL'])).default([...REGISTER_ORDER]),
  scopes: z.array(z.enum(['TOWN', 'COUNTRY', 'LIFE'])).default([...SCOPE_ORDER]),
  tags: z.array(z.string()).default([]),
  sort: z.enum(FEED_SORTS).default('hot'),
  window: z.enum(FEED_WINDOWS).default('week'),
  following: z.boolean().default(false),
});

/**
 * Filters live in the URL (spec §6.1: shareable) and are mirrored to
 * localStorage (spec §6.1: persist across sessions). The URL always wins — a
 * link someone sent you must show what they saw, not what you last looked at.
 */
export function filtersFromSearchParams(params: URLSearchParams): FeedFilters {
  // Returns undefined rather than [] when the param is absent: Zod applies a
  // `.default()` only for `undefined`, so an empty array would be taken as a
  // deliberate "select nothing" and leave every chip unselected while the feed
  // still showed everything.
  const list = (key: string): string[] | undefined => {
    const raw = params
      .getAll(key)
      .flatMap((value) => value.split(','))
      .map((value) => value.trim())
      .filter(Boolean);

    return raw.length > 0 ? raw : undefined;
  };

  const parsed = filtersSchema.safeParse({
    country: params.get('country')?.toUpperCase() ?? '',
    town: params.get('town') ?? '',
    types: list('type')?.map((value) => value.toUpperCase()),
    scopes: list('scope')?.map((value) => value.toUpperCase()),
    tags: list('tag')?.map((value) => value.toLowerCase()),
    sort: params.get('sort') ?? undefined,
    window: params.get('window') ?? undefined,
    following: params.get('following') === 'true',
  });

  // A malformed URL falls back to defaults rather than an error page: someone
  // hand-editing a query string should get the feed, not a stack trace.
  return parsed.success ? parsed.data : { ...DEFAULT_FILTERS };
}

/**
 * Only non-default values are written, so a plain `/` stays clean and a shared
 * link carries exactly what the sender changed.
 */
export function filtersToSearchParams(filters: FeedFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.country) params.set('country', filters.country);
  if (filters.country && filters.town) params.set('town', filters.town);

  if (filters.types.length !== REGISTER_ORDER.length) {
    params.set('type', filters.types.join(','));
  }
  if (filters.scopes.length !== SCOPE_ORDER.length) {
    params.set('scope', filters.scopes.join(','));
  }
  if (filters.tags.length > 0) params.set('tag', filters.tags.join(','));

  if (filters.sort !== DEFAULT_FILTERS.sort) params.set('sort', filters.sort);
  if (filters.sort === 'top' && filters.window !== DEFAULT_FILTERS.window) {
    params.set('window', filters.window);
  }
  if (filters.following) params.set('following', 'true');

  return params;
}

/** Query params for the API call. The shapes differ from the URL, so this is separate. */
export function filtersToApiQuery(filters: FeedFilters): Record<string, string> {
  const query: Record<string, string> = { sort: filters.sort };

  if (filters.country) query.country = filters.country;
  if (filters.country && filters.town) query.town = filters.town;
  if (filters.types.length && filters.types.length < REGISTER_ORDER.length) {
    query.type = filters.types.join(',');
  }
  if (filters.scopes.length && filters.scopes.length < SCOPE_ORDER.length) {
    query.scope = filters.scopes.join(',');
  }
  if (filters.tags.length) query.tag = filters.tags.join(',');
  if (filters.sort === 'top') query.window = filters.window;
  if (filters.following) query.following = 'true';

  return query;
}

/** Whether anything is narrowed — drives the "Clear filters" affordance. */
export function hasActiveFilters(filters: FeedFilters): boolean {
  return (
    filters.country !== '' ||
    filters.tags.length > 0 ||
    filters.types.length !== REGISTER_ORDER.length ||
    filters.scopes.length !== SCOPE_ORDER.length ||
    filters.following
  );
}

/**
 * How many filters are narrowing the feed right now.
 *
 * Used on the compact filter bar, where the reader cannot see the controls: a
 * bare "Filters" button gives no hint that three of them are already on.
 */
export function activeFilterCount(filters: FeedFilters): number {
  let count = 0;

  if (filters.country) count += 1;
  if (filters.town) count += 1;
  if (filters.types.length !== REGISTER_ORDER.length) count += 1;
  if (filters.scopes.length !== SCOPE_ORDER.length) count += 1;
  count += filters.tags.length;

  return count;
}

export const FILTERS_STORAGE_KEY = 'tgbi.feed-filters';

/** Persisted between sessions (spec §6.1) — but never the following tab or tags. */
export function loadStoredFilters(): Partial<FeedFilters> | null {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;

    const parsed = filtersSchema.partial().safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function storeFilters(filters: FeedFilters): void {
  try {
    // Tags and the following tab are navigational rather than preferences —
    // restoring them a week later would look like a bug.
    const { tags: _tags, following: _following, ...persisted } = filters;
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(persisted));
  } catch {
    // Private browsing or a full quota — filters simply do not persist.
  }
}
